import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronLeft, Plus, Edit2, Trash2, Upload, Image, Video, FileText,
  Music, Download, Copy, Check, Search,
} from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import {
  Button, Card, Spinner, EmptyState, Badge, SearchInput, PaginationBar,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Input, Textarea, Select, DeleteConfirmModal,
} from '../../../components/ui'

interface MediaItem {
  id: number; title: string; description: string | null; type: string
  file_url: string; file_name: string; file_size: number; mime_type: string
  caption_text: string | null; target_platforms: string | null
  download_count: number; category_name: string | null; category_color: string | null
  uploaded_by_name: string; created_at: string
}

interface Category { id: number; name: string; slug: string; color: string | null }

const typeIcons: Record<string, React.ElementType> = { image: Image, video: Video, audio: Music, document: FileText }
const typeLabels: Record<string, string> = { image: 'Imagem', video: 'Vídeo', audio: 'Áudio', document: 'Documento' }

export default function CommunityArsenalPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<MediaItem[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 20, last_page: 1 })

  // Modal importar
  const [modalOpen, setModalOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [form, setForm] = useState({ title: '', description: '', category_id: '', caption_text: '', target_platforms: '' })

  // Delete
  const [deleteItem, setDeleteItem] = useState<MediaItem | null>(null)
  const [deleting, setDeleting] = useState(false)

  const fetchItems = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { search, page, per_page: perPage }
      if (typeFilter) params.type = typeFilter
      if (categoryFilter) params.category_id = categoryFilter
      const res = await axios.get(buildApiUrl('/v1/community/media'), { params })
      setItems(res.data.data)
      if (res.data.meta) setMeta(res.data.meta)
    } catch {} finally { setLoading(false) }
  }, [search, page, perPage, typeFilter, categoryFilter])

  useEffect(() => { fetchItems() }, [fetchItems])
  useEffect(() => { setPage(1) }, [search, typeFilter, categoryFilter, perPage])
  useEffect(() => {
    axios.get(buildApiUrl('/v1/community/categories')).then(res => setCategories(res.data.data)).catch(() => {})
  }, [])

  function openImport() {
    setSelectedFile(null)
    setForm({ title: '', description: '', category_id: '', caption_text: '', target_platforms: '' })
    setModalOpen(true)
  }

  async function handleImport(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedFile || !form.title.trim()) return
    setUploading(true)
    setUploadProgress(0)
    try {
      const fd = new FormData()
      fd.append('file', selectedFile)
      fd.append('title', form.title)
      if (form.description) fd.append('description', form.description)
      if (form.category_id) fd.append('category_id', form.category_id)
      if (form.caption_text) fd.append('caption_text', form.caption_text)
      if (form.target_platforms) fd.append('target_platforms', form.target_platforms)
      await axios.post(buildApiUrl('/v1/community/media'), fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100))
        },
        timeout: 600000,
      })
      toast('success', 'Mídia importada')
      setModalOpen(false)
      fetchItems()
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Erro ao importar')
    } finally { setUploading(false); setUploadProgress(0) }
  }

  async function handleDelete() {
    if (!deleteItem) return
    setDeleting(true)
    try {
      await axios.delete(buildApiUrl(`/v1/community/media/${deleteItem.id}`))
      toast('success', 'Mídia removida')
      setDeleteItem(null)
      fetchItems()
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Erro')
    } finally { setDeleting(false) }
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  }

  const formatDate = (d: string) => new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: undefined })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/community/dashboard')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Arsenal</h1>
            <p className="text-gray-500 mt-1">Biblioteca de mídias da comunidade</p>
          </div>
        </div>
        <Button onClick={openImport}>
          <Upload className="w-4 h-4 mr-2" />
          Importar Mídia
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <SearchInput placeholder="Buscar por título..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1">
          {['', 'image', 'video', 'audio', 'document'].map(t => (
            <button key={t} onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${typeFilter === t ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {t ? typeLabels[t] : 'Todos'}
            </button>
          ))}
        </div>
        {categories.length > 0 && (
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5">
            <option value="">Todas categorias</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="p-4 animate-pulse">
              <div className="h-32 bg-gray-100 rounded-lg mb-3" />
              <div className="h-4 bg-gray-100 rounded w-3/4 mb-2" />
              <div className="h-3 bg-gray-50 rounded w-1/2" />
            </Card>
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Image className="w-8 h-8" />}
            title="Nenhuma mídia"
            description="Importe imagens, vídeos e textos prontos pra sua comunidade compartilhar."
            action={<Button onClick={openImport}><Upload className="w-4 h-4 mr-2" />Importar</Button>}
          />
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {items.map(item => {
              const TypeIcon = typeIcons[item.type] || FileText
              return (
                <Card key={item.id} className="p-4">
                  {/* Preview */}
                  {item.type === 'image' ? (
                    <div className="h-32 rounded-lg bg-gray-50 overflow-hidden mb-3">
                      <img src={item.file_url} alt={item.title} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="h-32 rounded-lg bg-gray-50 flex items-center justify-center mb-3">
                      <TypeIcon className="w-10 h-10 text-gray-300" />
                    </div>
                  )}

                  {/* Info */}
                  <h3 className="text-sm font-semibold text-gray-800 truncate">{item.title}</h3>
                  {item.description && <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{item.description}</p>}

                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <Badge variant="gray" size="sm">{typeLabels[item.type] || item.type}</Badge>
                    {item.category_name && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${item.category_color || '#666'}20`, color: item.category_color || '#666' }}>
                        {item.category_name}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <div className="text-xs text-gray-400">
                      <span>{formatSize(item.file_size)}</span>
                      <span className="mx-1">·</span>
                      <span>{formatDate(item.created_at)}</span>
                      <span className="mx-1">·</span>
                      <span className="flex items-center gap-0.5 inline-flex"><Download className="w-3 h-3" />{item.download_count}</span>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setDeleteItem(item)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-error-500 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
          <PaginationBar page={page} perPage={perPage} total={meta.total} lastPage={meta.last_page} onPageChange={setPage} onPerPageChange={n => { setPerPage(n); setPage(1) }} />
        </>
      )}

      {/* Modal importar */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="lg">
        <ModalHeader onClose={() => setModalOpen(false)}>Importar Mídia</ModalHeader>
        <form onSubmit={handleImport}>
          <ModalBody className="space-y-4">
            {/* Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Arquivo *</label>
              {selectedFile ? (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center">
                    {(() => { const I = typeIcons[selectedFile.type?.split('/')[0] || 'document'] || FileText; return <I className="w-5 h-5 text-brand-500" /> })()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{formatSize(selectedFile.size)}</p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setSelectedFile(null); if (fileRef.current) fileRef.current.value = '' }}>Trocar</Button>
                </div>
              ) : (
                <button type="button" onClick={() => fileRef.current?.click()}
                  className="w-full flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed border-gray-300 rounded-xl hover:border-brand-400 hover:bg-brand-25 transition-colors">
                  <Upload className="w-8 h-8 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Clique para selecionar</span>
                  <span className="text-xs text-gray-400">Imagem, vídeo, áudio ou documento (máx 50MB)</span>
                </button>
              )}
              <input ref={fileRef} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) { setSelectedFile(f); if (!form.title) setForm(prev => ({ ...prev, title: f.name.replace(/\.[^.]+$/, '') })) } }} />
            </div>

            <Input label="Título *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Corte - Educação 30s" required />
            <Textarea label="Descrição" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Pra que serve este material" rows={2} />

            {categories.length > 0 && (
              <Select label="Categoria" value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
                options={[{ value: '', label: 'Sem categoria' }, ...categories.map(c => ({ value: String(c.id), label: c.name }))]} />
            )}

            <Textarea label="Legenda pronta" value={form.caption_text} onChange={e => setForm(f => ({ ...f, caption_text: e.target.value }))}
              placeholder="Texto que o membro copia e cola ao postar" rows={3} hint="O membro toca pra copiar esta legenda" />
          </ModalBody>
          {uploading && (
            <div className="px-6 pb-2 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Enviando...</span>
                <span className="text-xs font-medium text-brand-600">{uploadProgress}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-brand-500 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
              </div>
              {uploadProgress === 100 && <p className="text-xs text-gray-400">Processando no servidor...</p>}
            </div>
          )}
          <ModalFooter>
            <Button variant="ghost" onClick={() => setModalOpen(false)} disabled={uploading}>Cancelar</Button>
            <Button type="submit" loading={uploading} disabled={!selectedFile || !form.title.trim()}>
              <Upload className="w-4 h-4 mr-2" />
              Importar
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <DeleteConfirmModal open={!!deleteItem} onClose={() => setDeleteItem(null)} onConfirm={handleDelete}
        title="Remover mídia" message={`Remover "${deleteItem?.title}"? A mídia não aparecerá mais no arsenal.`} />
    </div>
  )
}
